<template>
  <div class="header-container is-flex print-bg" ref="headerContainer">
    <!-- Social Media -->
    <social-media v-if="showSocialMedia" :project="project" :projectHeight="projectHeight"></social-media>
    <!-- Creator Menu-->
    <div v-if="!seenCreatorMenu" class="header-top-bar no-print">
      <!-- back button-->
      <div
          class="project-back-button is-pulled-left cursor-pointer is-flex is-size-7 is-uppercase has-text-grey-light has-text-weight-semibold"
          @click="goBack"
          tabindex="0"
          @keypress.enter.space="goBack">
        <i class="fal fa-chevron-left"></i>
        {{ $t("projectPage.back") }}
      </div>
      <!-- feature button-->
      <div v-if="isAdmin" class="is-flex mt-2">
        <div class="feature-button button is-small is-size-7 has-text-weight-semibold is-uppercase"
             @click="featureProject(projectPriority)">
          <i :class="{fal: !hasPriority, fas: hasPriority}" class="fa-star has-text-primary"></i>
          &nbsp;Feature&nbsp;<span class="is-hidden-mobile">Project</span>
        </div>
        <label for="projectPriority" class="visually-hidden">Priority:</label>
        <select class="priority-size cursor-pointer" tabindex="0" id="projectPriority" name="Priority"
                v-model="projectPriority">
          <option tabindex="0" value="0">0</option>
          <option value="1">1</option>
          <option value="2">2</option>
          <option value="3">3</option>
          <option value="4">4</option>
        </select>
      </div>
      <div class="header-button-container">
        <!-- RegionProjectCurator section-->
        <region-dropdown v-if="regionProjectCurator" :isHeader="true" @updateRegion="updateRegion"></region-dropdown>
        <button v-if="regionProjectCurator" @click="addProjectToRegion" :disabled="!selectedRegion"
                class="button cursor-pointer is-primary is-size-7 is-uppercase is-small has-text-weight-semibold mt-2">
          Add project to region
        </button>
        <!-- submit button-->
        <div v-show="showSubmitButton">
            <button judge-on-boarding="step-5"
                    class="submit-button button header-button is-flex is-size-7 is-uppercase is-small has-text-weight-semibold"
                    :class="{ 'disabled': disableSubmitButton || (isProjectSubmitted && !canUnsubmit)}">
              <span v-show="!isProjectSubmitted" @click="submitProject">
                {{
                  isRegistrationFormProject
                      ? $t("projectPage.confirmRegistrationFormSubmission")
                      : $t("projectPage.confirmSubmission")
                }}
              </span>
              <span v-show="isProjectSubmitted && !canUnsubmit">
                {{ $t("projectPage.submissionConfirmed") }}
              </span>
              <span v-show="isProjectSubmitted && canUnsubmit"
                    @click="unSubmitProject">
                {{
                  isRegistrationFormProject
                      ? $t("projectPage.confirmRegistrationFormUnSubmission")
                      : $t("projectPage.confirmUnSubmission")
                }}
              </span>
            </button>
        </div>
        <!-- approver options-->
        <div v-if="showApproverButtons" class="approval-container is-flex">
          <div v-if="isProjectPending"
               v-bind:class="{ 'approve-button': !isProjectApproved, 'approved-button': isProjectApproved || isProjectDeclined}"
               class=" approve-button button header-button is-flex is-size-7 is-uppercase is-small has-text-weight-semibold"
               @click="approveProject">
            <span>{{ $t("projectPage.approveSubmission") }}</span>
          </div>

          <div v-if="isProjectPending"
               v-bind:class="{ 'decline-button': !isProjectDeclined, 'declined-button': isProjectDeclined || isProjectApproved}"
               class=" decline-button button header-button is-flex is-size-7 is-uppercase is-small has-text-weight-semibold"
               @click="declineProject">
            <span>{{ $t("projectPage.declineSubmission") }}</span>
          </div>
        </div>
        <!-- awards button-->
        <div v-if="judgeAwards.length > 0">
          <div class="give-award-button button header-button is-size-7 is-uppercase is-small has-text-weight-semibold"
               tabindex="0" @keypress.enter.space="awardDropDown" @click="awardDropDown">
            <i class="fal fa-award is-size-6"></i>&nbsp;{{ $t("projectPage.award.give") }}
            <i v-show="!showAwards" class="fas fa-angle-down caret"></i>
            <i v-show="showAwards" class="fas fa-angle-up caret"></i>
          </div>
          <div v-show="showAwards">
            <div class="award-select">
              <span class="dropdown-item" v-for="award in judgeAwards" :key="award.id" tabindex="0"
                    @keypress.enter.space="awardSelected(award)" @click="awardSelected(award)">{{ award.name }}</span>
            </div>
          </div>
        </div>

        <!-- certificate button-->
        <div v-if="showDownloadCertificateButton">
          <div class="certificate-button button header-button is-size-7 is-uppercase is-small has-text-weight-semibold"
               @click="certificateDropdown">
            <i class="fal fa-award is-size-6 fas fa-angle-down caret"></i>&nbsp;
            {{ $isYSC ? $t('projectPage.award.get') : $t('projectPage.award.download') }}
            <i v-show="!showCertificates" class="fas fa-angle-down caret"></i>
            <i v-show="showCertificates" class="fas fa-angle-up caret"></i>
          </div>
          <div v-show="showCertificates">
            <div class="certificate-select">
              <option class="dropdown-item" v-for="certificate in certificates" :key="certificate.id"
                      @click="downloadCertificate(certificate)">{{ certificate.event }}
              </option>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="seenCreatorMenu" class="creator-menu is-flex" :class="{'creator-menu-sticky' : fromStartButtonTemplate}">
      <div
          class="project-back-button is-flex cursor-pointer is-size-7 is-uppercase has-text-grey-light has-text-weight-semibold"
          @click="goBack">
        <i class="fal fa-chevron-left" qa="back-btn"></i>
        {{ $t("projectPage.back") }}
      </div>
      <div class="getting-started is-flex is-hidden-mobile" v-if="fromStartButtonTemplate && !$isYSC">
        <div class="button is-small" @click="openVideoTour" v-if="$isMake">
          <i class="fal fa-play-circle"></i>&nbsp; {{ $t("projectPage.gettingStarted") }}
        </div>
      </div>
      <div class="save-cancel-delete is-flex">
        <div v-if="showPrivatePublicToggle" class="toggle-wrap is-flex">
          <toggle-switch v-model="togglePrivate" :reverse="true"
                         qa="status_toggle_edit_mode"></toggle-switch>
          <span class="toggle-text has-text-grey-light is-uppercase">{{
              isPublic ? $t('projectPage.publicMode') : $t('projectPage.privateMode')
            }}</span>
        </div>
        <div v-if="error" class="project-error title-error help has-text-danger">* {{ error }}</div>
        <span v-if="isProjectDeletable" @click="confirmDelete" tabindex="0" @keypress.enter.space="confirmDelete"
              class="delete-btn cursor-pointer" qa="delete-project-btn"><i class="fal fa-trash"></i></span>
        <div v-if="!fromStartButtonTemplate" class="edit-cancel button is-small is-outlined is-primary is-uppercase"
             @click="cancelEdit" tabindex="0" @keypress.enter.space="cancelEdit" qa="cancel-project-btn">{{
            $t("cancel")
          }}
        </div>
        <div v-bind="toolTip" class="tool-tip">
          <div class="is-uppercase button edit-save is-small is-primary has-text-weight-semibold"
               :class="{'is-loading': loadingPosts, 'save-fr' : $i18n.locale === 'fr'}"
               :disabled="isSaving || !enableSave" @click="saveProjectWithCategories" tabindex="0" @keypress.enter.space="saveProjectWithCategories"
               qa="save-project-btn">
            <span v-if="!isSaving">{{ $t("projectPage.save") }}</span>
            <span v-if="isSaving">{{ $t("projectPage.saving") }}</span>
          </div>
        </div>
      </div>
    </div>
    <div v-if="isMobile && fromStartButtonTemplate && seenCreatorMenu && !$isYSC" class="getting-started is-flex">
      <div class="button is-small" @click="openVideoTour">
        <i class="fal fa-play-circle"></i>&nbsp;{{ $t("projectPage.gettingStarted") }}
      </div>
    </div>
    <admin-toolbar v-if="isAdmin || isCurator" :isPrivate="isPrivate" :edit="edit" :projectRole="projectRole"
                   :isSnapshot="isSnapshot"></admin-toolbar>
    <coordinator-toolbar v-if="!isSnapshot && (isRoleAnonCoordinator || isAdmin)"></coordinator-toolbar>
    <div v-if="isSnapshot && isStudentUser && isTeamMember && !isProjectEditable" class="snapshot-banner">
      <p class="snapshot-message">This is a snapshot of your project. To make
        changes, please click <a :href="`${$webRoot}/project/${project.sourceProjectID}`" _target="blank">here</a> to go to the master project.</p>
    </div>
    <div v-if="!isSnapshot && $store.getters.getSubmissions.length > 0 && isStudentUser && isTeamMember && !isProjectEditable" class="snapshot-banner">
      <p class="snapshot-message">This is your project. To view the snapshot
        submitted to a fair please click on the name of the fair in the submission status on the right.</p>
    </div>
    <div v-if="isSnapshot && isStudentUser && !isTeamMember && !isProjectEditable" class="snapshot-banner">
      <p class="snapshot-message">This is a read-only view of the project, submitted to {{getSnapshotFair}} fair.
        To view the project, please click <a :href="`${$webRoot}/project/${project.sourceProjectID}`" _target="blank">here.</a></p>
    </div>
    <div v-if="!isSnapshot && $store.getters.getSubmissions.length > 0 && isStudentUser && !isTeamMember && !isProjectEditable" class="snapshot-banner">
      <p class="snapshot-message">This is the master project. To view a read-only version of what was submitted to the fair(s),
        please click on the name of the fair in the submission status area on the right of the project.</p>
    </div>
    <div v-if="showSubmissionDeadlineText" class="submission-deadline">
      <p class="submission-deadline-date"> <span class="submission-deadline-text">{{ $t('projectPage.submissionDeadline') }} |</span> {{ submissionDeadlineDate }}</p>
    </div>
    <div v-if="showCategorySponsor" class="category-sponsor-container">
      <a :href="projectCategorySponsorLink" target="_blank" class="category-sponsor">
        <img :src="projectCategorySponsorImage" alt="Category Sponsor">
      </a>
      <div class="category-sponsor-text">
        <p>Proud Sponsor Of:</p>
        <p class="has-text-weight-bold"> {{ projectCategory }}</p>
      </div>
    </div>
    <div class="project-header is-flex"
         :class="{'project-header-edit' : edit && !isAdmin, 'project-header-start' : fromStartButtonTemplate && !isAdmin} ">
      <div class="header-content is-flex" :class="{ 'with-description' : hasDescription }">
        <!-- Header Left -->
        <div class="cover-photo-container is-flex">
          <!-- Ribbons -->
          <div v-for="(award, index) in awards"
               class="award" :data-balloon="$t(award.name)"
               data-balloon-pos="up-right"
               v-bind:style="{'z-index': (awards.length - index),
               'right': rightAlignAwards(index, awards.length) }"
               :key="award.id">
            <img v-if="award.showRibbon" :src="award.image" :alt="$t(award.name)"/>
          </div>
          <!-- Project Label -->
          <project-label v-if="project.isTemplate" :project='project'></project-label>
          <div v-if="!edit"
               @click="toggleProjectPhoto"
               class="cover-photo gtm-proj-image cursor-pointer"
               :style="projectImageBackground"
               qa="cover_image">
            <Swoosh v-if="project.displaySwoosh" :swooshText="project.caption" :background="project.swooshBackground" :style="cssVars"></Swoosh>
          </div>
          <image-upload v-if="edit" ref="imageUpload" :image="newImage" :callback="setImage"
                        :generated="project.imageGenerated" class="cover-photo cover-photo-edit"
                        gtmSuccess="gtm-proj-image" proxyWidth="625" proxyHeight="500"
                        qa="upload-cover-image-btn"></image-upload>
        </div>
        <!-- Header Right -->
        <div class="header-right " :class="{'header-right-edit' : edit}">
          <!-- Top Right -->
          <div class="top-right is-flex" :class="{'top-right-award' : sponsoredAward && !edit}">
            <!-- Awards -->
            <div v-if="sponsoredAward && !edit" class="sponsored-award is-size-7 has-text-weight-bold">
              {{ $t("projectPage.award.sponsored", { challenge: sponsoredAward.name }) }}
              <a :href="sponsoredAward.sponsorUrl">
                <img :src="sponsoredAward.sponsorImage"/>
              </a>
            </div>
            <div v-if="!isSnapshot" class="project-edit-buttons" :class="{'private-mode' : isPrivate}">
              <div v-if="canEditProject && isPrivate && !edit && !$isEngCom"
                   class="private-only is-flex is-pulled-left">
                <toggle-switch v-if="!$isOhioScience" v-model="togglePrivate" :reverse='true' @input="handleToggle"
                               qa="status_toggle"></toggle-switch>
                <span v-if="!$isOhioScience"
                      class="toggle-text has-text-grey-light is-uppercase">{{ $t("projectPage.private") }}</span>
              </div>
              <collect-pin v-if="project.showCollectPin" class="is-hidden-widescreen" :project="project"
                           showSideSwap="true" :pinSize="60"></collect-pin>
              <div v-if="canEditProject && !edit" class="edit-btn">
                <button-tpl :class="{'mt-4': project.showCollectPin}" @click="editProject" type="edit-button"
                             size="small" qa="edit_btn">{{ $t("edit") }}
                </button-tpl>
              </div>
              <div v-if="canExportProject" class="export-btn">
                <button-tpl @click="exportProject" type="export-button" size="small" qa="export_btn">Export</button-tpl>
              </div>
              <div v-if="!isPrivate && !project.showCollectPin">
                <report v-if="!edit && (!canEdit || isAdmin) && !(isAdmin && sponsoredAward)"
                        :project="project"></report>
              </div>
            </div>
          </div>
          <div v-if="showFairID" class="fair-id">
            <p> {{ fairID }} </p>
          </div>
          <!-- Project Title -->
          <div v-if="!edit" class="project-title title is-4 is-size-5-mobile is-marginless has-text-weight-bold">
            {{ this.project.title }}
          </div>
          <div v-if="edit" class="project-title" style="position: relative;">
            <input :disabled="disableInput" class="input" ref="title" maxlength="100"
                   :placeholder="$t('projectPage.title')" v-model.trim="newTitle" data-lpignore="true"
                   qa="project-title-input">
            <div class="is-pulled-right has-text-grey-light is-size-7"
                 :class="{'help has-text-danger': newTitle.length > 99}"> {{ 100 - newTitle.length }} <span
                v-if="isMobile">{{ $t("projectPage.chars") }}</span><span v-else>{{
                $t("projectPage.characters")
              }}</span> {{ $t("projectPage.remaining") }}
            </div>
            <div v-if="edit && hasTitleError" class="title-error is-pulled-left help is-danger is-marginless">
              {{ $t("projectPage.titlePlaceholder") }}
            </div>
          </div>
          <!-- Project Description -->
          <div v-if="seenDescription && !edit" ref="description" class="project-description"
               :class="{'seeing-more': isSeeingMore}">
            <markdown-span style="margin: 0;" :isHeader="true" :content="project.description"></markdown-span>
            <div v-if="needSeeMoreButton & !isSeeingMore" class="bottom-gradient"></div>
            <button v-if="needSeeMoreButton" @click="toggleSeeMore" class="see-more-button">{{ seeMoreOrLess }}
              <i v-if="!isSeeingMore" class="fal fa-chevron-down" aria-hidden="true"></i>
              <i v-if="isSeeingMore" class="fal fa-chevron-up" aria-hidden="true"></i>
            </button>
          </div>
          <div v-if="edit" class="project-description-board-edit">
            <div class="project-description-edit">
              <label id="description">{{ $t("projectPage.description") }}</label>
              <div role="textbox" :class="{'disable-field': disableInput}" aria-multiline="true"
                   aria-labelledby="description" aria-required="true" class="textarea-div" contenteditable="true"
                   :placeholder="'Description'" @paste.prevent="onPaste" @input="updateNewDescription"
                   v-html="htmlDescription"></div>
              <div>
                <p v-if="descriptionWordCountLimit"
                   class="is-pulled-right has-text-grey-light is-size-7 help"
                   :class="{'help has-text-danger': descriptionWordCountLimitCheck}">
                  {{ descriptionWordCountLimit - htmlDescriptionWordCount }} {{ $t("projectPage.words") }} {{ $t("projectPage.remaining") }}
                </p>
                <p v-if="edit && descriptionErrorMessage" class="is-pulled-left title-error help is-danger">
                  {{ descriptionErrorMessage }}
                </p>
              </div>
            </div>
          </div>
          <!-- Project Meta -->
          <section class="project-meta is-flex"
                   :class="{ 'project-meta-edit': edit, 'add-margin': !getHtmlDescription }">
            <!-- Project Creator -->
            <template v-if="showTeam">
              <h2 class="meta-title is-uppercase">{{ $t("projectPage.team") }}</h2>
              <div class="creator-and-team-wrapper is-flex gtm-proj-members">
                <div class="project-creator is-flex gtm-proj-creator">
                  <div class="add-member-icon" :data-balloon="teamToolTip" data-balloon-pos="up">
                    <i @click="editTeam" tabindex="0" @keypress.enter.space="editTeam"
                       class="fal fa-plus-circle has-text-primary" aria-hidden="true" qa="add_team_member_btn"></i>
                  </div>
                </div>
                <!-- Teams -->
                <team ref="team" class="team-members cursor-pointer is-flex" :class="{'team-members-edit' : edit}"
                      :project="project" :avatar="avatar" :editMode="edit" @createOrEdit="createOrEdit"
                      @showOwners="showOwners()"></team>
              </div>
            </template>
          </section>
          <!-- Tags & Categories-->
          <span v-if="categoryError" class="category-error help has-text-danger">{{
              $t("projectPage.error.oneCategory")
            }}</span>
          <section v-if="!$isGirlsAndSteam || isAdmin" class="tags-and-categories is-flex">
            <div class="project-categories" :class="edit ? 'edit' : 'is-flex wrap-categories'">
              <project-categories :key="categoryKey" ref="projectCategories" :project="project" :fairID="this.fairID" :isRoleAdmin="isRoleAdmin"
                                  :edit="edit" @updateChosenCategories="updateChosenCategories"></project-categories>
            </div>
            <project-tags v-if="isShowTags" class="project-tags" :class="edit ? 'edit' : 'is-flex wrap-tags'" :project="project"
                          :edit="edit" :chosenTags="chosenTags" @updateChosenTags="updateChosenTags"
                          :tagsysc="this.tagsysc"></project-tags>
          </section>
          <div v-if="isTemplateProject && !edit && (canEdit || isRoleAnonCoordinator)" class="template-container">
            <h2 class="meta-title">TEMPLATE</h2>
            <button @click="showTemplateSettingsModal">Settings <i class="fas fa-cog"></i></button>
            <button @click="showShareTemplateModal">Share <i class="fas fa-share-square"></i></button>
            <button @click="goToProjectsCreated">Projects Created <i class="fal fa-clone"></i></button>
          </div>
        </div>
      </div>

      <!-- Meta Mobile-->
      <meta-counts v-if="!edit && !isPrivate" class="meta-counts is-hidden-tablet"
                   :project="this.project"></meta-counts>
    </div>
    <!-- Project Photo Modal -->
    <div class="project-photo-modal modal is-active is-5by4" v-if="showProjectPhoto" @click="toggleProjectPhoto">
      <div class="modal-background"></div>
      <div class="modal-content">
        <p class="image">
          <img :src="projectImage" :alt="project.title" v-on:click.stop>
        </p>
      </div>
      <button class="modal-close is-large" aria-label="close"></button>
    </div>
    <confirm ref="confirmDelete" title="Delete Board" text="Are you sure you want to delete this Board?"
             yesText="Yes, Delete" gtmSuccess="gtm-project-delete" @yes="deleteProject(true)"></confirm>
    <confirm ref="confirmDelete" :title="$t('projectPage.deleteProjectTitle')"
             :text="$t('projectPage.deleteProjectDescription')" :yesText="$t('projectPage.yesDelete')"
             gtmSuccess="gtm-project-delete" @yes="deleteProject(true)"></confirm>
    <modal-custom v-if="showConfirmPublishModal && !hasConfirmedPublic" :hasFooter="true"
                  :title="$t('projectActions.confirmPublishTitle')" :text="$t('projectActions.publishConfirm')"
                  :successText="$t('projectActions.publish')" :cancelText="$t('cancel')" @modalSuccess="finishSaving"
                  @modalCancel="cancelPublish"></modal-custom>
    <modal-categories v-if="showCategoryModal" @modalSuccess="finishSaving" @modalCancel="categoriesModalClose"
                      subText="Before publishing, select categories to help others discover your project"></modal-categories>
    <modal-start-template v-if="showSaveFromPrivateModal" class="confirm-visibility" :isPrivate="togglePrivate"
                          :chosenCategories="chosenCategories" @save="finishSaving" @cancel="cancelSaveFromPrivateModal"
                          :isSubmitProjectCall="isSubmitProjectCall" @saveCategories="saveCategories"
                          @close="cancelSaveFromPrivateModal"></modal-start-template>

    <modal-custom v-if="showStartQuestionsModal" class="confirm-visibility"
                  :title="$t('projectPage.startQuestionModalTitle')"
                  :templateID="project.sourceProjectID" :projectID="project.id"
                  :hasClose="true" :hasFooter="true" :successText="$t('submit')" :cancelText="$t('cancel')"
                  :questions="startQuestions"
                  @modalSuccess="showStartQuestionsModal = false" @modalCancel="showStartQuestionsModal = false"
                  @modalClose="showStartQuestionsModal = false">
    </modal-custom>
    <modal-custom v-if="showSubmitProjectModal && !isProjectSubmitted" class="confirm-visibility oas-submit"
                  :title="$t('projectPage.confirmSubmissionModal')" :text=submitMessage
                  :templateID="project.sourceProjectID" :projectID="project.id"
                  :hasClose="true" :hasFooter="true" :successText="$t('submit')" :cancelText="$t('cancel')"
                  :questions="submitQuestions"
                  :submissions="$store.getters.getSubmissions"
                  @modalSuccess="submitProjectNow" @modalCancel="closeSubmitProjectModal"
                  @modalClose="closeSubmitProjectModal">
    </modal-custom>
    <modal-custom v-if="informToWaitForFairProjects" class="confirm-visibility"
                  :title="'Submission Status'" :text="pleaseWaitForFairsToCreate"
                  :hasClose="true" :hasFooter="true" :successText="'Ok'"
                  @modalSuccess="closeFairWarningModal" @modalCancel="closeFairWarningModal"
                  @modalClose="closeFairWarningModal">
    </modal-custom>
    <modal-custom v-if="showSubmitProjectModal && isProjectSubmitted" class="confirm-visibility"
                  :title="$t('projectPage.confirmUnsubmissionModal')"
                  :templateID="project.sourceProjectID" :projectID="project.id"
                  :hasClose="true" :hasFooter="true" :successText="$t('unsubmit')" :cancelText="$t('cancel')"
                  @modalSuccess="unSubmitProjectNow" @modalCancel="closeSubmitProjectModal">
    </modal-custom>

    <modal-custom v-if="showApproveProjectModal && !isProjectApproved && !isProjectDeclined" class="confirm-visibility"
                  :title="$t('projectPage.confirmApprovalModal')" :text="$t('projectPage.confirmApprovalSection')"
                  :hasClose="true"
                  :hasFooter="true"
                  :successText="$t('projectPage.approveSubmission')" :cancelText="$t('cancel')"
                  @modalSuccess="approveOrDeclineProject('Approved')"
                  @modalCancel="closeApproveProjectModal" @modalClose="closeApproveProjectModal"></modal-custom>

    <modal-custom v-if="showDeclineProjectModal && !isProjectApproved && !isProjectDeclined" class="confirm-visibility"
                  :title="$t('projectPage.confirmDeclineModal')" :text="$t('projectPage.confirmDeclineSection')"
                  :hasClose="true"
                  :hasFooter="true"
                  :isDeclined="true"
                  :showTextBox="true"
                  :successText="$t('projectPage.declineSubmission')" :cancelText="$t('cancel')"
                  @modalSuccess="approveOrDeclineProject('Declined')"
                  @modalCancel="closeDeclineProjectModal" @modalClose="closeDeclineProjectModal"
                  @selectedReason="onClickChild"></modal-custom>
    <modal-custom
        v-if="showTemplateWarningModal"
        :hasFooter="true"
        :title="'Warning Project Template'" :text="'You are about to edit a Project Template.'"
        :cancelText="$t('OK')"
        @modalCancel="showTemplateWarningModal= false"
    ></modal-custom>
    <modal-custom v-if="isAwardOptionSelected" class="confirm-visibility"
                  :title="$t('projectPage.confirmSubmissionModal')" :text="awardConfirmMessage"
                  :hasClose="true" :hasFooter="true" :successText="$t('yes')" :cancelText="$t('no')"
                  @modalSuccess="giveAward" @modalCancel="closeAwards" @modalClose="closeAwards">
    </modal-custom>
    <modal-custom v-if="showProjectSubmissionWarningModal" class="confirm-visibility"
                  :title="'Incomplete Registration'" :text="warningProjectSubmissionText"
                  :hasClose="true" :hasFooter="true" :successText="'Ok'"
                  @modalSuccess="showProjectSubmissionWarningModal = false"
                  @modalClose="showProjectSubmissionWarningModal = false">
    </modal-custom>
    <div class="modal" :class="{'is-active': showLoadingModal && isSaving && !showSaveFromPrivateModal}">
      <div class="modal-background"></div>
      <div class="modal-content saving-modal">
        <preloader class="preloader" :displayText="$t('projectPage.projectSavingMessage')" :lightTextColor="true"
                   mode='animate'></preloader>
      </div>
    </div>
    <project-template-modal v-if="!$isEngCom" :project="project" ref="templateSettingsModal"></project-template-modal>
    <share-template-modal v-if="!$isEngCom" :project="project" ref="shareTemplateModal"></share-template-modal>
  </div>
</template>

<script>
import admin from '@/api/admin'
import projects from '@/api/projects'

import ModalStartTemplate from '../ModalStartTemplate'
import state from '../state'
import ShareMenu from '../ShareDropdown'

import AdminToolbar from './AdminToolbar'
import CoordinatorToolbar from './CoordinatorToolbar'
import mixin from './header'
import MetaCounts from './MetaCounts'
import ProjectCategories from './ProjectCategories'
import ProjectTags from './ProjectTags'
import SocialMedia from './SocialMedia'
import Team from './Teams'
import UserCards from './UserCards'
import SubmissionStatusCards from './SubmissionStatusCards'

import CollectPin from '@/components/Controls/CollectPin'
import Confirm from '@/components/Modals/Confirm'
import ButtonTpl from '@/components/Controls/ButtonTemplate'
import ImageUpload from '@/components/Inputs/ImageUpload'
import JudgeRubricCard from '@/components/SectionProjectView/Header/JudgeRubricCard'
import MarkdownSpan from '@/components/Controls/MarkdownSpan'
import ModalCustom from '@/components/Modals/Custom'
import ModalCategories from '@/components/Modals/ProjectCategories'
import Preloader from '@/components/Controls/Preloader'
import ProjectLabel from '@/components/Controls/ProjectLabel'
import ProjectTemplateModal from '@/components/Modals/ProjectTemplateSettings'
import RegionDropdown from '@/components/Controls/RegionDropdown'
import Report from '@/components/UIReport'
import ShareTemplateModal from '@/components/Modals/ShareTemplate'
import ToggleSwitch from '@/components/Controls/ToggleSwitch'
import Swoosh from '@/components/SectionProjectView/Swoosh'

import breakpoints from '@/libs/breakpoints'
import marked from '@/libs/marked'
import session from '@/libs/session'
import socket from '@/libs/socket'

import auth from '@/mixins/auth'
import componentState from '@/mixins/component-state'
import unsavedContent from '@/mixins/unsaved-content'

import debounce from 'lodash/debounce'
import i18n from '@/i18n'
import JsPDF from 'jspdf'

import displayTypes from '@/mixins/project-display-types'
import userTypes from '@/mixins/user-types'
import projectTemplates from '@/mixins/project-templates'
import { demoProjectIDs } from '../../../mixins/demo-data'
import authLib from '@/libs/auth'

export default {
  components: {
    AdminToolbar,
    CollectPin,
    Confirm,
    CoordinatorToolbar,
    ButtonTpl,
    ImageUpload,
    JudgeRubricCard,
    MetaCounts,
    ModalCustom,
    MarkdownSpan,
    ModalCategories,
    ModalStartTemplate,
    Preloader,
    ProjectTemplateModal,
    ProjectLabel,
    ProjectCategories,
    ProjectTags,
    RegionDropdown,
    Report,
    SocialMedia,
    ShareTemplateModal,
    ShareMenu,
    Team,
    ToggleSwitch,
    UserCards,
    SubmissionStatusCards,
    Swoosh
  },
  props: ['checkCanSave', 'projectLoading', 'projectHeight', 'tagsysc', 'classId', 'isSnapshot', 'isJacAdmin', 'isTeamMember'],
  mixins: [auth, componentState, mixin, unsavedContent, displayTypes, userTypes, projectTemplates],
  data: function () {
    return {
      awardToBeGiven: '',
      categoryError: false,
      categoryKey: '',
      error: '',
      fairID: '',
      hasPriority: false,
      hasConfirmedPublic: false,
      isSaveCancelled: false,
      isFirstImageSet: true,
      isProjectPending: false,
      isProjectApproved: false,
      isProjectDeclined: false,
      isSeeingMore: false,
      isUserApprover: false,
      isAwardOptionSelected: false,
      isCertificateOptionSelected: false,
      selectedRegion: null,
      reasonToSend: '',
      certificates: [],
      showCertificates: false,
      isCertificateSelected: false,
      selectedCertificate: '',
      needSeeMoreButton: false,
      projectPriority: 0,
      projectTemplate: null,
      promoterMenuError: '',
      promoterMenuInfo: '',
      projectRegion: null,
      renderOwners: false,
      showAwards: false,
      showLoadingModal: false,
      showStartQuestionsModal: false,
      showSubmitProjectModal: false,
      showCategoryModal: false,
      showSaveFromPrivateModal: false,
      showApproveProjectModal: false,
      showDeclineProjectModal: false,
      showTemplateWarningModal: false,
      startQuestions: [],
      submitQuestions: [],
      teamToolTip: '',
      ysc2022EventId: 12,
      warningProjectSubmissionText: '',
      showProjectSubmissionWarningModal: false,
      informToWaitForFairProjects: false,
      isSubmitProjectCall: false,
      templateQuestionGrouping: 1,
      declinedSSD: false,
      htmlDescription: ''
    }
  },
  mounted: function () {
    if (this.project && this.project.categories.length !== 0) {
      this.chosenCategories = this.project.categories
    }
    this.checkProjectDescriptionHeight()
    const params = { id: this.project.sourceProjectID }
    this.$store.dispatch('getTemplateForProject', params).then((response) => {
      if (response) {
        this.projectTemplate = response
      } else {
        this.projectTemplate = null // Default to null
      }
    }).catch(() => {
      this.projectTemplate = null
    })
    this.$store.dispatch('getProjectStatus', { projectID: this.project.id }).then((response) => {
      if (response) {
        this.isProjectPending = response.pending
        this.isProjectDeclined = response.declined
        this.isProjectApproved = response.approved
      } else {
        this.isProjectPending = false // Default to false
        this.isProjectDeclined = false // Default to false
        this.isProjectApproved = false // Default to false
      }
    })
    this.$store.dispatch('getProjectPostCounts', this.project.id).then((response) => {
    })
    this.$store.dispatch('isUserApprover', { userID: this.currentUser.id }).then((response) => {
      if (response) {
        this.isUserApprover = response.isUserApprover
      } else {
        this.isUserApprover = false // Default to false
      }
    })
    if (this.$isIsef) {
      this.$store.dispatch('getIsefFairID', this.project.id).then((response) => {
        if (response) {
          this.fairID = response
        } else {
          this.fairID = ''
        }
      })
    }
    if (this.$isYSC && (this.isProjectCreator)) {
      this.$store.dispatch('getRegionForProject', this.project.id).then((response) => {
        if (response) {
          this.projectRegion = response
        } else {
          this.projectRegion = null // Default to null
        }
      }).catch(() => {
        this.projectRegion = null
      })
    }

    projects.getCertificateForProject(this.project.id).then((response) => {
      if (response) {
        this.certificates = response.data.result.certificates
      } else {
        this.certificates = [] // Default to empty array
      }
    }).catch(() => {
      this.certificates = []
    })
    const data = {
      templateID: this.project.sourceProjectID,
      excludeAnswered: true,
      context: 'start'
    }
    this.$store.dispatch('getTemplateCustomerQuestions', data).then((response) => {
      const { questions } = response
      if (questions) {
        this.startQuestions = questions
        if (this.startQuestions?.length > 0) {
          const requiredQuestions = this.startQuestions.filter((question) => !question.optional)
          if (this.startQuestions?.length > 0 && (this.fromTemplate || this.project?.tags?.length < requiredQuestions?.length)) {
            if (!this.$isOhioScience) {
              this.showStartQuestionsModal = true
            }
          }
        }
      } else {
        this.startQuestions = []
        this.showStartQuestionsModal = false
      }
    }).catch(() => {
      this.startQuestions = []
      this.showStartQuestionsModal = false
    })

    if (this.isRegistrationFormProject) {
      this.$store.dispatch('getPollUserOptionSummaries', { projectId: this.project.id })
    }
  },
  watch: {
    edit: function () {
      if (this.edit) {
        this.editFields()
        var projectData = this._pop(this.headerDataKey)
        if (projectData !== null) {
          if (projectData.id) {
            this.newTitle = projectData.title
            this.newDescription = projectData.description
            this.htmlDescription = marked(projectData.description)
            this.newCaption = projectData.caption
          }
        }
      } else {
        this._setUnsavedContent(false)
      }
    },
    editState: function (isEdit) {
      if (isEdit) {
        this._set(state.project.header.edit, false)
        this.editMode()
      }
    },
    isSaved: function () {
      if (this.isSaved) {
        this.hasConfirmedPublic = !this.togglePrivate
        this.$emit('projectSaved')
      }
    },
    'newTitle.length': function (value) {
      if (!this.isSaved && !this.projectLoading) {
        this._setUnsavedContent((value || 0) > 0)
      }
    },
    'newDescription.length': function (value) {
      if (!this.isSaved && !this.projectLoading) {
        this._setUnsavedContent((value || 0) > 0)
      }
    },
    'newImage.length': function (value) {
      if (this.isFirstImageSet) {
        this.isFirstImageSet = false
        return
      }
      if (!this.isSaved && !this.projectLoading) {
        this._setUnsavedContent((value || 0) > 0)
      }
    },
    'project.priority': function (priority) {
      this.hasPriority = priority > 0
      this.projectPriority = priority
    }
  },
  computed: {
    awardConfirmMessage: function (awardName) {
      return i18n.t('projectPage.award.confirmAwardMessage-1') + this.awardToBeGiven +
          i18n.t('projectPage.award.confirmAwardMessage-2') + ' ' + i18n.t('projectPage.award.confirmAwardMessage-3')
    },
    awards: function () {
      if (this.project.awards?.length === 0) {
        const awards = []
        this.project.snapshots?.forEach((snapshot) => {
          if (snapshot.awards?.length > 0) {
            awards.push(...snapshot.awards)
          }
        })
        return awards
      }
      return this.project.awards
    },
    canEditProject: function () {
      return this.isAdmin || this.projectRole === 'anonAdmin' || this.projectRole === 'anonCoordinator' || (this.isProjectEditable && this.canEdit)
    },
    canExportProject: function () {
      return (this.isAdmin || ['owner', 'co-owner'].includes(this.projectRole)) && this.$isYSC
    },
    canUnsubmit: function () {
      return (this.isAdmin || this.projectRole === 'anonAdmin') && (this.$isYSC || this.$isIsef) // The YSC check can be removed when we add fairs to YSC. 'Unsubmit' functionality will need to be updated
    },
    loggedIn: function () {
      return this.$store.getters.loggedIn
    },
    currentUser: function () {
      return this.$store.getters.currentUser ? this.$store.getters.currentUser : {}
    },
    categoryIds: function () {
      return this.chosenCategories ? this.chosenCategories.map((category) => category.id) : []
    },
    disableInput: function () {
      return this.$isIsef && this.fairID !== ''
    },
    descriptionModified: function () {
      return this.project.description !== this.newDescription
    },
    editState: function () {
      return this._get(state.project.header.edit, false)
    },
    enableSave: function () {
      return this.$isIsef ? !this.loadingPosts : (!this.loadingPosts && this.isHeaderValid && this.saveTemplateRequirements && !this.descriptionErrorMessage)
    },
    getHtmlDescription: function () {
      if (this.project && this.project.description) {
        return marked(this.project.description)
      }
    },
    hasDescription: function () {
      return this.project.description && this.project.description.length
    },
    descriptionWordCountLimitCheck: function () {
      return this.descriptionWordCountLimit ? this.htmlDescriptionWordCount > this.descriptionWordCountLimit : false
    },
    descriptionWordCountLimit: function () {
      switch (this.project.sourceProjectID) {
        case this.YSC2023EnglishTemplate:
        case this.YSC2023FrenchTemplate:
          return 100
        default:
          return false
      }
    },
    descriptionErrorMessage: function () {
      if (this.edit) {
        if (this.newDescription.length < 2) {
          return this.$t('projectPage.descriptionPlaceholder')
        }
        if (this.descriptionWordCountLimitCheck) {
          return this.$t('projectPage.descriptionWordLengthError')
        }
      }
    },
    hasTitleError: function () {
      if (!this.togglePrivate) {
        const canPublish = this.newTitle.length > 2
        return !canPublish || this.renameProjectRequired
      }
      return this.renameProjectRequired || this.invalidNewTitle
    },
    hasLongTitleError: function () {
      return this.newTitle && this.newTitle.length >= 180
    },
    invalidNewTitle: function () {
      return this.newTitle === '' || this.newTitle === 'Untitled'
    },
    isFlasfRegion: function () {
      return this.projectRegion.id === 58
    },
    isHeaderValid: function () {
      return !this.hasTitleError && !this.hasLongTitleError
    },
    isMobile: function () {
      return breakpoints.isMobile()
    },
    isDemoProject: function () {
      return demoProjectIDs.includes(this.project.id)
    },
    isProjectEditable: function () {
      return this.project.isEditable
    },
    isTabletAndUp: function () {
      return breakpoints.isTabletAndUp()
    },
    isProjectCreator: function () {
      return this.project.user.id === this.currentUser.id
    },
    isProjectDeletable: function () {
      // Temporarily disable delete for ISEF
      // https://magicboard.monday.com/boards/250786593/pulses/4271413634
      // ToDo remove this later
      if (this.$isIsef && !this.isAdmin) {
        return false
      }

      return this.isAdmin || !this.project.isTemplate
    },
    isProjectSubmitted: function () {
      return this.project.submitted
    },
    isRoleAnonCoordinator: function () {
      return this.projectRole === 'anonCoordinator'
    },
    isRoleAdmin: function () {
      return this.projectRole === 'admin'
    },
    isSubmittable: function () {
      return this.projectTemplate != null && this.projectTemplate && this.projectTemplate.showSubmit
    },
    isTemplateProject: function () {
      return this.project.isTemplate
    },
    isRegistrationFormProject: function () {
      return this.project.displayType === 'registrationForm'
    },
    isPollCompleted: function () {
      return this.$store.getters.isPollCompleted
    },
    showDownloadCertificateButton: function () {
      return this.certificates?.length > 0 && !this.project.isPrivate && !this.project.isHidden && (['owner', 'co-owner'].includes(this.projectRole) || this.isAdmin)
    },
    judgeAwards: function () {
      return this.$store.getters.userAwards ? this.$store.getters.userAwards : []
    },
    loadingPosts: function () {
      return this._get(state.posts.loading)
    },
    mustHaveCategories: function () {
      return !this.$isIsef && !this.$isGirlsAndSteam && this.project.requireCategoriesForSave
    },
    projectLabel: function () {
      return this.project.type
    },
    publicSubtitle: function () {
      return this.togglePrivate ? i18n.t('projectPage.onlyVisibleToYou') : i18n.t('projectPage.visibleToCommunity')
    },
    projectRole: function () {
      return this._get(state.project.role)
    },
    renameProjectRequired: function () {
      return this.newTitle === 'Untitled Project' || (this.project.title === 'Untitled Project' && this.newTitle === '')
    },
    regionProjectCurator: function () {
      return this.$isYSC && (this.currentUser.isRegionProjectCurator || this.isAdmin)
    },
    rightAlignForAward: function () {
      return 23
    },
    saveTemplateRequirements: function () {
      return this.fromTemplate ? this.descriptionModified && this.titleModified : true
    },
    seeMoreOrLess: function () {
      return this.isSeeingMore ? i18n.t('projectPage.seeLess') : i18n.t('projectPage.seeMore')
    },
    seenCreatorMenu: function () {
      return this.edit
    },
    showApproverButtons: function () {
      return !this.edit && this.isProjectPending && this.isUserApprover
    },
    showCategorySelectModal: function () {
      return this.chosenCategories.length === 0 && this.mustHaveCategories
    },
    showFairID: function () {
      return this.$isIsef && !this.edit
    },
    showSubmissionDeadlineText: function () {
      return this.submissionDeadlineDate && this.userCanSubmitProject
    },
    showCategorySponsor: function () {
      switch (process.env.APP_NAME.toLowerCase()) {
        case 'isef':
          return this.projectCategorySponsorImage && this.projectCategorySponsorLink && this.projectCategory
        default:
          return false
      }
    },
    projectCategorySponsorImage: function () {
      return this.project.categories?.[0]?.sponsorImage
    },
    projectCategorySponsorLink: function () {
      return this.project.categories?.[0]?.sponsorLink
    },
    projectCategory: function () {
      return this.project.categories?.[0]?.name
    },
    submissionDeadlineDate: function () {
      switch (this.project.sourceProjectID) {
        // Use template ID from mixin to return the date
        // Example:
        // case this.ISEF2023Template:
        //   return 'April 26, 2023, 11:59PM ET'
        default:
          return false
      }
    },
    canSubmitNonFairsProject: function () {
      return this.isSubmittable && (this.userCanSubmitProject || this.isRegistrationFormProject) && !this.$isOhioScience
    },
    userCanSubmitProject: function () {
      return this.isAdmin || ['owner', 'co-owner', 'admin', 'anonAdmin'].includes(this.projectRole)
    },
    studentsSectionsCompleted: function () {
      return this.postMilestoneCount && (this.postMilestoneCount === this.postCompletedCount)
    },
    postMilestoneCount: function () {
      return this.$store.getters.getPostCounts[this.project.id] && this.$store.getters.getPostCounts[this.project.id].milestonePostCount
    },
    postCompletedCount: function () {
      return this.$store.getters.getPostCounts[this.project.id] && this.$store.getters.getPostCounts[this.project.id].completedPostCount
    },
    disableSubmitButton: function () {
      return this.canSubmitNonFairsProject && this.isRegistrationFormProject && !this.isPollCompleted
    },
    showSocialMedia: function () {
      return !this.edit && !this.isRegistrationFormProject
    },
    spaceBetweenAwards: function () {
      return (this.$isIsef || this.$isYSC) ? 30 : 20
    },
    submitMessage: function () {
      if (this.$isOhioScience) {
        if (this.allowShowQuestions && !this.isOhioSSDDeclined) {
          if (this.projectTemplate.projectID === this.BIOTemplate && !this.isOhioSSDFair) {
            return this.$t('projectPage.oasBIOSubmitMessage')
          } else if (this.isOhioSSDFair) {
            return this.$t('projectPage.oasSSDFairMessage')
          } else {
            return this.$t('projectPage.oasSubmitMessage')
          }
        } else {
          return this.$t('projectPage.confirmProjectSubmission')
        }
      } else if (this.$isProjectboard) {
        return this.$t('projectPage.projectBoardSubmitMessage')
      } else {
        return this.projectTemplate && this.projectTemplate.submitMessage
      }
    },
    sponsoredAward: function () {
      return this.project.awards && this.project.awards.length > 0 &&
      this.project.awards[0].isSponsored ? this.project.awards[0] : false
    },
    toolTip: function () {
      const tooltip = {}
      if (!this.enableSave && !this.loadingPosts) {
        tooltip['data-balloon'] = this.descriptionErrorMessage ? this.descriptionErrorMessage : this.$t('projectPage.saveTooltip')
        tooltip['data-balloon-pos'] = 'down-right'
        if (this.isMobile) {
          tooltip['data-balloon-length'] = 'medium'
        }
      }
      return tooltip
    },
    titleModified: function () {
      return this.newTitle !== this.project.title
    },
    showTeam: function () {
      return !this.isRegistrationFormProject || this.isAdmin
    },
    usersNotCompletedQuestions: function () {
      return this.$store.getters.usersNotCompletedQuestions
    },
    allowProjectSubmission: function () {
      return (this.$isOhioScience && this.usersNotCompletedQuestions.length === undefined) || !this.$isOhioScience
    },
    eligibleFairsForProjectSubmission: function () {
      return this.$store.getters.eligibleFairs || []
    },
    eligibleToSubmitForFair: function () {
      return (this.eligibleFairsForProjectSubmission?.length > 0) && this.$isOhioScience
    },
    showSubmitButton: function () {
      return (!this.isSnapshot && this.eligibleToSubmitForFair) || this.canSubmitNonFairsProject || (this.isDemoProject && this.loggedIn)
    },
    pleaseWaitForFairsToCreate: function () {
      return 'Thank you for your submission. The status of your project submission will be displayed on the right side of your project, but this will take a few minutes! You can continue using the site in the meantime.'
    },
    swooshTextSizeFactor: function () {
      const textSize = this.project.caption.length || 0
      return textSize > 30 ? 0.8 : 1
    },
    cssVars: function () {
      return {
        '--swooshTextSizeFactor': this.swooshTextSizeFactor
      }
    },
    allowShowQuestions: function () {
      return (this.$store.getters.getSubmissions.filter(s => s.status !== 'STATUS_NOT_SUBMITTED').length === 0 && this.project.sourceProjectID === this.BIOTemplate) || this.project.sourceProjectID === this.ScienceDayTemplate
    },
    enableCategoryModal: function () {
      return (this.$isOhioScience || this.$isYSC || this.$isIsef) && this.chosenCategories.length === 0
    },
    bioDeclinedProject: function () {
      return this.$store.getters.getSubmissions.length !== 0 && this.project.sourceProjectID === this.BIOTemplate && this.$store.getters.getSubmissions[0].status === 'STATUS_DECLINED'
    },
    showPrivatePublicToggle: function () {
      return (!this.$isOhioScience && !this.$isIsef && !this.$isAjas && !this.$isEngCom) || this.isAdmin
    },
    isStudentUser: function () {
      return this.currentUser.type?.includes(this.UserTypeStudent)
    },
    checkOhioIfEligibleToDSDBSEFFair: function () {
      return this.$isOhioScience && this.eligibleFairsForProjectSubmission.length !== undefined && (this.isOhioDSDFair || this.isOhioBSEFFair) && this.project.sourceProjectID === this.ScienceDayTemplate
    },
    isOhioSSDFair: function () {
      return this.eligibleFairsForProjectSubmission?.[0]?.abbreviation === 'SSD'
    },
    isOhioDSDFair: function () {
      return this.eligibleFairsForProjectSubmission?.[0]?.abbreviation === 'DSD'
    },
    isOhioBSEFFair: function () {
      return this.eligibleFairsForProjectSubmission?.[0]?.abbreviation === 'BSEF'
    },
    isOhioSSDDeclined: function () {
      this.$store.getters.getSubmissions.forEach((submission) => {
        if (submission.fair.abbreviation === 'SSD' && submission.status === 'STATUS_DECLINED') {
          this.declinedSSD = true
        }
      })
      if (this.declinedSSD) {
        return true
      }
    },
    getSnapshotFair: function () {
      return this.$store.getters.getSubmissions?.[0]?.name
    },
    isShowTags: function () {
      return !this.$isIsef
    }
  },
  methods: {
    addProjectToRegion: function () {
      this.promoterMenuError = ''
      this.promoterMenuInfo = ''
      const params = { projectID: this.project.id, regionID: this.selectedRegion.id }
      this.$store.dispatch('addProjectToRegion', params).then(() => {
        this.promoterMenuInfo = 'Successfully added project to region.'
      }, (error) => {
        this.promoterMenuError = 'Failed to add project to region. Please try again.'
        this.error = error.message
      })
    },
    approveProject: function () {
      this.showApproveProjectModal = true
    },
    approveOrDeclineProject: function (value) {
      if (value === 'Approved') {
        this.projectApproved = true
        this.closeApproveProjectModal()
      } else {
        this.projectApproved = false
        this.closeDeclineProjectModal()
      }
      this.$store.dispatch('approveOrDeclineProject', {
        projectID: this.project.id,
        status: value,
        reason: this.reasonToSend
      }).then((response) => {
        if (response.approved) {
          this.$store.dispatch('generateQRCode', { projectID: this.project.id })
        }
        this.isProjectDeclined = response.declined
        this.isProjectApproved = response.approved
      }, (error) => {
        this.error = error.message
      })
    },
    allPostsEdited: function () {
      var allEdited = false
      for (var id in this.$store.state.post.posts) {
        allEdited = this.$store.state.post.posts[id].saved
      }
      return allEdited
    },
    awardDropDown: function () {
      this.showAwards = !this.showAwards
    },
    awardSelected: function (award) {
      this.awardToBeGiven = award?.name
      this.awardID = award?.id
      this.isAwardOptionSelected = true
    },
    checkProjectDescriptionHeight: function () {
      this.$nextTick(function () {
        if (this.$refs.description) {
          if (this.$refs.description.scrollHeight > this.$refs.description.clientHeight) {
            this.needSeeMoreButton = true
          } else {
            this.needSeeMoreButton = false
          }
        }
      })
    },
    cancelSaveFromPrivateModal: function () {
      this.showSaveFromPrivateModal = false
      this.isSaving = false
      this.isSaveCancelled = true
    },
    cancelEdit: function () {
      this.edit = false
      this._stopEditing()
      this.categoryError = false
      this.isSaved = true
      this.togglePrivate = this.project.isPrivate
      this.checkProjectDescriptionHeight()
    },
    cancelPublish: function () {
      this.togglePrivate = true
      this.toggleConfirmPublishModal()
      this.isSaving = false
    },
    categoriesModalClose: function () {
      this.togglePrivate = true
      this.showCategoryModal = false
      this.isSaveCancelled = true
      this.isSaving = false
    },
    categoriesModalSuccess: function (newCategories) {
      this.chosenCategories = newCategories
      this.$refs.projectCategories.chosenCategories = this.chosenCategories
      this.showCategoryModal = false
    },
    certificateSelected: function (certificate) {
      this.certificateToBeGiven = certificate?.event
      this.certificateID = certificate?.id
      this.isCertificateOptionSelected = true
    },
    closecertificates: function () {
      this.showCertificates = false
      this.isCertificateOptionSelected = false
    },
    closeAwards: function () {
      this.showAwards = false
      this.isAwardOptionSelected = false
    },
    confirmDelete: function () {
      this.$refs.confirmDelete.show()
    },
    closeSubmitProjectModal: function () {
      this.showSubmitProjectModal = false
      this.isSaveCancelled = true
    },
    closeApproveProjectModal: function () {
      this.showApproveProjectModal = false
    },
    closeDeclineProjectModal: function () {
      this.showDeclineProjectModal = false
    },
    createOrEdit: function (hasTeam) {
      if (hasTeam) {
        this.teamToolTip = i18n.t('projectPage.editTeam')
      } else {
        this.teamToolTip = i18n.t('projectPage.createTeam')
      }
    },
    declineProject: function () {
      this.showDeclineProjectModal = true
    },
    deleteProject: function (shouldUpdateRouter) {
      this.$emit('isDeleting', true)
      this.error = ''
      this.$store.dispatch('deleteProject', this.project).then((project) => {
        socket.delete('project', this.id)
        if (this.$isOhioScience) {
          this.$store.dispatch('clearEligibleFairs')
        }
        if (shouldUpdateRouter) {
          if (window.history.length < 3) {
            this.$router.push({ name: 'home' })
          } else {
            this.$router.push('/viewAll/?profile=mine')
          }
        }
      }, (messages) => {
        this.clicked = false
        if (messages && messages.length >= 0) {
          console.error(`Delete error: ${messages[0]}`)
        }
        this.error = 'An error occurred while deleting project' + this.project.Title + '.  Please try again'
        this.$emit('isDeleting', false)
      })
    },
    editFields: function () {
      if (this.project.id) {
        // Check if we're coming from the search page create project prompt
        this.newTitle = session.get('promptFromSearch') ? session.get('promptFromSearch') : this.project.title
        this.newCaption = this.project.caption
        if (this.isPrivate && this.project.imageUrl === '') {
          this.newImage = ''
        } else {
          this.newImage = this.project.imageUrl
        }
        this.newDescription = this.project.description
        this.htmlDescription = marked(this.project.description)
      }
    },
    editProject: function () {
      if (this.project.isTemplate) {
        this.showTemplateWarningModal = true
      }
      this.isSaved = false
      this.editMode()
    },
    exportProject: function () {
      this.$cookie.set('token', authLib.getToken(), 100000000)
      window.open(`${process.env.API_ROOT}/projects/${this.project.id}/export?event_slug=${process.env.APP_NAME.toLowerCase()}`, '_blank')
      this.$cookie.delete('token')
    },
    editMode: debounce(function () {
      this.edit = true
      this._startEditing()
      this.isSaved = !this.edit
    }, 200, { leading: true, trailing: false }),
    editTeam: function () {
      this.$refs.team.$refs.editModal.show()
    },
    featureProject: function (newPriority) {
      const data = {
        priority: parseInt(newPriority),
        username: this.project.user.userName,
        description: this.project.description
      }
      admin.updateProject(this.project.id, data).then((response) => {
        this.projectPriority = response.data.result.project.priority
        this.hasPriority = this.projectPriority > 0
      }, (error) => {
        this.error = error.message
      })
    },
    finishSaving: function (params) {
      if (this.showConfirmPublishModal) {
        if (!this.fromStartButtonTemplate) {
          if (!this.edit) {
            this.switchToPublic()
          }
        }
        this.toggleConfirmPublishModal()
      } else if (this.showCategoryModal) {
        this.categoriesModalSuccess(params)
        if (!this.togglePrivate && !this.hasConfirmedPublic) {
          this.showConfirmPublishModal = true
          this.showCategoryModal = false
          return
        }
      } else if (this.showSaveFromPrivateModal && !this.$$isIsef) {
        this.saveFromPrivateModal(params)
      }
      setTimeout(() => {
        if (!this.isSaved) {
          this.isSaving = true
          this.showLoadingModal = true
        }
      }, 1000)
      if (this.fromStartButtonTemplate) {
        this.isSaving = false
        this.$emit('saveProject')
      } else {
        this.saveEdit()
        this.isSaved = true
        this.isSaving = false
        this.checkProjectDescriptionHeight()
      }
    },
    giveAward: function () {
      var arrR = this.awards.filter((award) => {
        return (award.id === this.awardID)
      }, [])

      // Only add award if it hasnt been given already
      if (arrR.length === 0) {
        this.$store.dispatch('giveProjectAward', { projectID: this.project.id, awardID: this.awardID })
      }

      this.closeAwards()
    },
    goBack: function () {
      if (this.classId !== undefined) {
        this.$router.push({ path: '/profile?activeTab=TabMyClasses&classId=' + this.classId })
      } else if (this.fromStartButtonTemplate) {
        this.$router.push({ name: 'home' })
      } else {
        if (this.edit && !this.fromStartButtonTemplate) {
          this.cancelEdit()
          return
        }
        if (window.history.length < 2) {
          this.$router.push({ name: 'home' })
        } else {
          this.$router.go(-1)
          localStorage.setItem('backFromProject', true)
        }
      }
    },
    downloadCertificate: function (certificate) {
      this.selectedCertificate = certificate
      this.toDataUrl(certificate.firebaseUrl, this.generateCertificate)
    },
    generateCertificate: function (certificate) {
      let fullName = this.currentUser.fullName
      if (fullName === '') {
        fullName = this.currentUser.username
      }
      const projectName = this.project.title
      const nameLocation = this.selectedCertificate.pageWidth * this.selectedCertificate.pagePercentage

      const doc = new JsPDF(this.selectedCertificate.orientation, 'mm', 'a4')

      doc.setFontSize(this.selectedCertificate.fontSize)
      doc.addImage(certificate, 'JPEG', 0, 0, this.selectedCertificate.certificateWidth, this.selectedCertificate.certificateHeight)

      if (fullName.length >= 17) {
        const names = fullName.split(' ')
        doc.text(names[0], nameLocation, this.selectedCertificate.firstNameLocation, 'center')
        doc.text(names[names.length - 1], nameLocation, this.selectedCertificate.lastNameLocation, 'center')
      } else {
        doc.text(fullName, nameLocation, this.selectedCertificate.fullNameLocation, 'center')
      }
      this.selectedCertificate.needsProjectTitle !== 0 ? doc.text(projectName, nameLocation, 148, 'center') : this.projectName = ''
      doc.save(fullName + ' Certificate.pdf')
    },
    handleToggle: function () {
      this.editFields()
      if (!this.project.requireCategoriesForSave || (this.project.categories && this.project.categories.length > 0)) {
        this.showConfirmPublishModal = true
      } else {
        this.showCategoryModal = true
        this.isSaved = false
      }
    },
    openVideoTour: function () {
      this.$store.dispatch('showVideoTourModal')
    },
    onClickChild (params) {
      var declineReason = params.reason
      if (declineReason[0] > 0) {
        this.$store.dispatch('enterDeclineReason', { projectID: this.project.id, messageID: declineReason[0] })
      }
      if (declineReason[1] !== '') {
        const body = this.$t('projectPage.messageBody1') + declineReason[2] + this.$t('projectPage.messageBody2') + declineReason[1]
        this.$store.dispatch('enterDeclineComment', { projectID: this.project.id, messageBody: body })
      }
      if (declineReason[2] !== '') {
        this.reasonToSend = declineReason[2]
      } else if (declineReason[2] === '' && declineReason[1] !== '') {
        this.reasonToSend = declineReason[1]
      } else if (declineReason[2] === '' && declineReason[1] === '') {
        this.error = 'Please enter or select reason for decline'
      }
    },
    onPaste (evt) {
      var clearText = event.clipboardData.getData('text/plain')
      document.execCommand('inserttext', false, clearText)
    },
    promoteProject: function (hide) {
      this.promoterMenuError = ''
      this.promoterMenuInfo = ''
      const params = { id: this.project.id, isHidden: hide }
      this.$store.dispatch('updateProjectHidden', params).then(() => {
      }, (error) => {
        this.promoterMenuError = 'Failed to promote project. Please try again.'
        this.error = error.message
      })
    },
    refreshKey: function () {
      return (Math.random() + 1).toString(36).substring(7)
    },
    rightAlignAwards: function (index, numberOfAwards) {
      return this.spaceBetweenAwards * (numberOfAwards - index) + this.rightAlignForAward + 'px'
    },
    saveProject: debounce(async function () {
      if (this.isSaving || !this.enableSave) {
        return
      }
      if (this.showCategorySelectModal && this.project.requireCategoriesForSave && !this.togglePrivate) {
        if (this.$isYSC || this.$isAjas) {
          if (this.project.displayType !== 'judgeTeam') {
            this.showCategoryModal = true
          } else {
            this.showLoadingModal = true
            this.isSaving = true
            this.finishSaving()
          }
        } else {
          this.showSaveFromPrivateModal = true
        }
        return
      }
      this.showLoadingModal = true
      this.isSaving = true
      try {
        this._set('errorSaving', false)
        if (!this.isHeaderValid || !this.checkCanSave()) {
          this.isSaving = false
          return
        }
        if (this.showCategorySelectModal && this.project.requireCategoriesForSave && !this.togglePrivate) {
          if (this.$isYSC || this.$isAjas) {
            this.showCategoryModal = true
          } else {
            this.showSaveFromPrivateModal = true
          }
          return
        }
        this.showSaveModals()
      } catch (error) {
        console.error('error is ' + error)
        this.isSaving = false
      }
    }, 700, { leading: true, trailing: false }),
    showSaveModals: function () {
      if (this.fromStartButtonTemplate && this.togglePrivate && !this.$isYSC && !this.$isAjas && !this.$isIsef && this.chosenCategories.length === 0) {
        this.showSaveFromPrivateModal = true
      } else if (!this.togglePrivate && !this.$isIsef) {
        if (this.chosenCategories.length > 0 || !this.showCategorySelectModal) {
          if (this.hasConfirmedPublic) {
            this.showLoadingModal = true
            this.finishSaving()
          } else {
            this.showConfirmPublishModal = true
            this.showLoadingModal = false
          }
        } else {
          this.showCategoryModal = true
        }
      } else {
        this.finishSaving()
      }
    },
    saveEdit: debounce(async function () {
      if (this.isSaved) {
        return
      }
      this.error = ''
      let newCategories = []
      // if the category section is hidden, save the current category.
      // This is to prevent removing categories from the parent for projects where the category is not muteable
      if (!this.$refs.projectCategories) {
        newCategories = this.project.categories.map((category) => category.id)
      } else {
        newCategories = this.categoryIds
      }
      const data = {
        id: this.project.id,
        tags: this.chosenTags,
        categories: newCategories,
        title: this.newTitle,
        description: this.newDescription,
        isPrivate: this.togglePrivate,
        goal: this.goal || this.project.goal,
        priority: this.project.priority || 0,
        details: this.project.details,
        caption: this.project.caption || this.newCaption,
        isImageChanged: false
      }
      if (this.$refs.imageUpload) {
        await this.uploadImage('projects', this.project.id).then((url) => {
          data.image = url
          if (url === '') {
            data.imageGenerated = true
          } else {
            data.isImageChanged = true
          }
        })
      }
      this.updateProject(data)
    }, 770, { leading: true, trailing: false }),
    setImage: function (value) {
      this.newImage = value
    },
    saveCategories: function (params) {
      this.chosenCategories = params.chosenCategories
      const data = {
        id: this.project.id,
        categories: this.categoryIds
      }
      this.isSaved = true
      this.$store.dispatch('updateProjectCategories', data).then((project) => {
        this.edit = false
        this.isSaving = false
        this.showSaveFromPrivateModal = false
        this.submitProject()
      }, (messages) => {
        this.isSaving = false
        if (messages && messages.length >= 0) {
          console.log('Save error ' + messages[0])
        }
        this.error = 'An error occurred while updating categories' + this.project.Title + '.  Please try again'
      })
    },
    showOwners () {
      this.renderOwners = !this.renderOwners
    },
    submitProject: function () {
      if (this.enableCategoryModal) {
        this.isSubmitProjectCall = true
        this.showSaveFromPrivateModal = true
      } else {
        if (this.$isOhioScience) {
          this.submitProjectsToFair()
        } else {
          this.submitUserProject()
        }
      }
    },
    saveProjectWithCategories: function () {
      if (this.enableCategoryModal) {
        this.showSaveFromPrivateModal = true
      } else {
        this.saveProject()
      }
      if (this.isDemoProject && !this.isAdmin) {
        alert('This project is just for demonstration purposes')
      }
    },
    submitUserProject: function () {
      if (!this.isRegistrationFormProject) {
        this.getTemplateQuestions()
      } else if (this.isRegistrationFormProject) {
        this.showSubmitProjectModal = true
      }
    },
    submitProjectsToFair: function () {
      this.checkMembersAnswers().then(() => {
        if (this.allowProjectSubmission && this.allowShowQuestions && !this.isOhioSSDDeclined) {
          this.getTemplateQuestions()
        } else {
          this.submitQuestions = []
          if (this.bioDeclinedProject) {
            this.showSubmitProjectModal = true
          } else if (this.isOhioSSDDeclined) {
            this.showSubmitProjectModal = true
          } else {
            this.showSubmitProjectModal = false
          }
        }
      })
    },
    getTemplateQuestions: function () {
      if (!this.checkOhioIfEligibleToDSDBSEFFair && this.isOhioSSDFair) {
        this.templateQuestionGrouping = 2
        this.getSSDAwards()
        this.getSSDScholarships()
      }
      const data = {
        templateID: this.project.sourceProjectID,
        excludeAnswered: false,
        context: 'submit',
        questionGroup: this.templateQuestionGrouping
      }
      this.$store.dispatch('getTemplateCustomerQuestions', data).then((response) => {
        const { questions } = response
        if (questions) {
          this.submitQuestions = questions
          if (this.$isOhioScience) {
            if (this.checkOhioIfEligibleToDSDBSEFFair && !this.isOhioSSDFair) {
              // only filter expired for non admins
              if (!this.isAdmin && this.eligibleFairsForProjectSubmission?.length === 1) {
                this.submitQuestions = this.submitQuestions.filter((question) => {
                  if (this.eligibleFairsForProjectSubmission[0].abbreviation === 'BSEF') {
                    return question.question === 'SD-customer-submit-for-bsef' || question.question === 'SD-customer-which-jac-district'
                  } else if (this.eligibleFairsForProjectSubmission[0].abbreviation === 'DSD') {
                    return question.question === 'SD-customer-submit-for-scienceday' || question.question === 'SD-customer-which-jac-district'
                  }
                })
              }
            }
            // ensure that no fair is already submitted
            const submittedFairs = this.$store.getters.getSubmissions.map((submission) => {
              if (submission.status === 'STATUS_SUBMITTED') {
                return submission.fair.abbreviation
              }
            })
            this.submitQuestions = this.submitQuestions.filter((question) => {
              let fairAbbreviation = null
              if (question.question === 'SD-customer-submit-for-scienceday') {
                fairAbbreviation = 'DSD'
              } else if (question.question === 'SD-customer-submit-for-bsef') {
                fairAbbreviation = 'BSEF'
              }
              return !submittedFairs.includes(fairAbbreviation)
            })
            // We need to remove this question if user has already answered question and this case will come if project is declined and user is resubmitting the projects for OAS fair
            this.project.tags.forEach((tag) => {
              if (tag.name.includes('DSD')) {
                this.submitQuestions = this.submitQuestions.filter((question) => {
                  return question.question !== 'SD-customer-which-jac-district'
                })
              }
            })
          }
          this.showSubmitProjectModal = true
        } else if ((this.$isIsef || this.$isSigmaXi) && this.submitQuestions.length === 0) {
          this.showSubmitProjectModal = true
        } else {
          this.submitQuestions = []
          this.showSubmitProjectModal = false
        }
      }).catch(() => {
        this.submitQuestions = []
        this.showSubmitProjectModal = false
      })
    },
    submitProjectNow: function (projectID, fairs) {
      if (this.$isOhioScience) {
        this.submitFairProjects(projectID, fairs)
      } else {
        this.submitUserProjectNow()
      }
    },
    closeFairWarningModal: function () {
      this.informToWaitForFairProjects = false
    },
    submitUserProjectNow: function () {
      this.closeSubmitProjectModal()
      const params = { id: this.project.id, submitted: true }
      this.$store.dispatch('updateProjectSubmitted', params).then(() => {
      }, (error) => {
        this.clicked = false
        this.error = error.message
      })
    },
    submitFairProjects: function (projectID, fairs) {
      this.closeSubmitProjectModal()
      const data = {
        fairs: fairs
      }
      this.$store.dispatch('createFairProject', { projectID: projectID, data }).then((success) => {
        this.informToWaitForFairProjects = true
        this.showSubmitProjectModal = false
      }, () => {
        this.error = this.$t('projectPage.failedToCreateProjectOnFair')
      })
    },
    switchToPublic: debounce(function () {
      this.togglePrivate = false
      const params = { id: this.project.id, isPrivate: this.togglePrivate }
      this.$store.dispatch('updateProjectPrivate', params).then(() => {
        this._setUnsavedContent(false)
        this._setHasUnsavedAttachments(false)
      })
    }, 770),
    saveFromPrivateModal: function (data) {
      this.chosenCategories = data.chosenCategories
      if (this.$refs.projectCategories) {
        this.$refs.projectCategories.chosenCategories = this.chosenCategories
      }
      this.togglePrivate = data.isPrivate
      this.showSaveFromPrivateModal = false
    },
    saveCertificate: function (certificate) {
      switch (this.projectRegion.id) {
        case 58: // Flasf region ID
          this.generateFlasfCertificate(certificate)
          break
        default:
          this.generateYSCCertificate(certificate)
      }
    },
    showTemplateSettingsModal: function () {
      this.$refs.templateSettingsModal.active = true
    },
    showShareTemplateModal: function () {
      this.$refs.shareTemplateModal.active = true
    },
    toDataUrl: function (url, callback) {
      var xhr = new XMLHttpRequest()
      xhr.onload = function () {
        var reader = new FileReader()
        reader.onloadend = function () {
          callback(reader.result)
        }
        reader.readAsDataURL(xhr.response)
      }
      xhr.open('GET', url)
      xhr.responseType = 'blob'
      xhr.send()
    },
    toggleSeeMore: function () {
      this.isSeeingMore = !this.isSeeingMore
      if (!this.isSeeingMore) {
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }
    },
    unSubmitProject: function () {
      this.showSubmitProjectModal = true
    },
    unSubmitProjectNow: function () {
      this.closeSubmitProjectModal()
      const params = { id: this.project.id, submitted: false }
      this.$store.dispatch('updateProjectSubmitted', params).then((response) => {
      }, (error) => {
        this.clicked = false
        this.error = error.message
      })
    },
    certificateDropdown: function () {
      this.showCertificates = !this.showCertificates
    },
    closeCertificates: function () {
      this.showCertificates = false
      this.isAwardOptionSelected = false
    },
    updateNewDescription: function (e) {
      this.newDescription = e.target.innerText
    },
    updateChosenCategories: function (value) {
      if (value.length) {
        this.categoryError = false
      }
      this.chosenCategories = value
    },
    updateChosenTags: function (value) {
      this.chosenTags = value
    },
    updateProject: function (data) {
      this.showLoadingModal = true
      this.isSaving = true
      this.$store.dispatch('updateProject', data).then((project) => {
        this.edit = false
        this._stopEditing()
        this.editFields()
        this.isSaved = true
        this.isSaving = false
        this.showLoadingModal = false
        this.checkProjectDescriptionHeight()
        if (!this.hasUnsaved && project.slug && this.$route.fullPath !== `${this.basePath}/${project.slug}`) {
          this.$router.replace({
            path: `${this.basePath}/${project.slug}`,
            query: Object.assign({}, this.$route.query, { r: this.refreshKey() })
          })
        }
      }, (messages) => {
        this.isSaving = false
        if (messages && messages.length >= 0) {
          console.log('Save error ' + messages[0])
        }
        this.error = 'An error occurred while updating project' + this.project.Title + '.  Please try again'
      })
    },
    updateRegion: function (newSelectedRegion) {
      this.selectedRegion = newSelectedRegion
    },
    goToProjectsCreated: function () {
      this.$router.push({ name: 'viewAll', query: { templateID: this.project.id, title: '' } })
    },
    checkMembersAnswers: function () {
      return new Promise((resolve, reject) => {
        this.$store.dispatch('checkCompletedMemberQuestions', {
          projectID: this.project.id,
          eventID: this.project.event.id
        }).then(() => {
          if (this.usersNotCompletedQuestions.length !== undefined) {
            let text = ''
            this.usersNotCompletedQuestions.forEach((userName, i) => {
              const userNameHyperlink = `<a href="${this.$webRoot}/profile/${userName}" target="_blank">${userName}</a>`
              if (i === 0) {
                text = userNameHyperlink
              } else if (i > 0 && i + 1 !== this.usersNotCompletedQuestions.length) {
                text = text.concat(', ' + userNameHyperlink)
              } else if (i + 1 === this.usersNotCompletedQuestions.length) {
                text = text.concat(' and ' + userNameHyperlink)
              }
            })
            const profile = 'Profile'
            const profilePageLink = `<a href="${this.$webRoot}/profile" target="_blank">${profile}</a>`
            this.warningProjectSubmissionText = `<span>The following <strong>Owners or Co-owners</strong> of your project have not completed the registration information: ${text}.<br/>To complete this information, project <strong>Owners and Co-owners</strong><br/>should visit their ${profilePageLink}.<br/>Please also ensure all members of your group have joined your project as Co-owners in the <strong>Teams</strong> section of your project.</span>`
            this.showProjectSubmissionWarningModal = true
          }
          resolve(true)
        }).catch(err => reject(err))
      })
    },
    getSSDAwards: function () {
      return new Promise((resolve, reject) => {
        const ssdAwardParams = { siteVisibility: 'OhioSSDAwards', limit: 0, offset: 0 }
        this.$store.dispatch('getAwards', ssdAwardParams).then(() => {
          resolve(true)
        }).catch(err => reject(err))
      })
    },
    getSSDScholarships: function () {
      const ssdScholarshipParams = { siteVisibility: 'OhioSSDScholarship', limit: 0, offset: 0 }
      this.$store.dispatch('getAwards', ssdScholarshipParams).then(() => {
      })
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~styles/utils.scss';

.side-cards-container {
  top: calc(83px);
  right: calc(50vw - 630px);
  position: fixed;
  z-index: 5;
  position: fixed;
  display: flex;
  flex-direction: column;
  height: 100vh;
}

.add-member-icon {
  color: $primary;
  margin-right: .5rem;
  font-size: 1.5rem;
  display: flex;
}

.add-team {
  margin-left: 0.5rem;
}

.awardOption {
  background-color: white;
}

.awardLink {
  color: black;
  font-size: 8px;
}

.award {
  position: absolute;
  z-index: 2;
  right: 50px;
  top: -6px;
  width: 50px;
  height: 51px;
}

.caret {
  padding-left: 9px;
  font-size: 1.5rem;
}

.creator {
  margin-right: 1rem;
  margin-top: 18px;
}

.category-sponsor {
  width: 15rem;
  @include mobile {
    width: 12rem;
  }
  &-text {
    padding: 0 0 10px 10px;
    @include mobile {
      font-size: 0.7rem;
    }
  }
  &-container {
    display: flex;
    align-items: flex-end;
    width: 100%;
    margin: 15px 0 0 0;
    font-size: 1rem;
  }
}

::v-deep .content-type {
  position: absolute;
  left: 0;
}

.creator-menu-sticky {
  position: fixed;
  top: 0;
  border-top: 52px solid #f2f2f2;
  z-index: 10;
  max-width: 800px;
  @include mobile {
    max-width: 100vw;
  }
}

.creator-and-meta {
  width: 75%;
  justify-content: space-between;
}

.creator-menu {
  padding: 0rem 1rem;
  background-color: white;
  font-size: 0.9rem;
  margin-top: 0;
  margin-top: 0.5rem;
  width: 100%;

  .project-back-button {
    margin: auto 0;
    align-items: center;

    i {
      margin-right: 5px;
    }

    &:hover {
      color: $link-hover !important;

      .fal {
        color: $link-hover;
      }
    }

    @include mobile {
      max-width: 100vw;
    }
  }

  .delete-btn {
    font-size: 0.7rem;
    margin-left: 1rem;
    color: #242424;
    font-size: 0.85rem;
    @include mobile {
      margin-left: .25rem;
    }
  }
}

.cover-photo-container {
  justify-content: center;
  max-height: 100%;
  position: relative;
  @include mobile {
    width: 100%;
    min-width: 100%;
  }
}

::v-deep .swoosh {
  width: calc(100% - 1.75rem);
  &-text {
    font-size: calc(1.4rem * var(--swooshTextSizeFactor));
  }
  @include mobile {
    clip-path: path('M -3 40 C 388 157 432 -42 851 75 L 855 151 L -3 151 Z');
    height: 45%;
    width: 100%;
    &-text {
      font-size: calc(1.4rem * var(--swooshTextSizeFactor));
    }
  }
  @media (max-width: 600px) {
    clip-path: path('M -3 42 C 292 107 304 -16 609 46 L 611 148 L -3 149 Z');
    height: 40%;
    &-text-container {
      height: 42%;
    }
  }
  @media (max-width: 550px) {
    clip-path: path('M 0 42 C 269 105 329 -8 569 42 L 574 148 L 0 148 Z');
    &-text-container {
      height: 42%;
    }
  }
  @media (max-width: 500px) {
    clip-path: path('M 0 42 C 269 105 276 -5 518 42 L 521 143 L 0 143 Z');
    height: 38%;
    &-text {
      line-height: 1.3rem;
    }
  }
  @media (max-width: 450px) {
    clip-path: path('M 0 42 C 216 96 229 9 467 42 L 469 142 L 0 142 Z');
  }
  @media (max-width: 400px) {
    clip-path: path('M 0 42 C 190 92 207 11 408 42 L 409 143 L 0 143 Z');
  }
  @media (max-width: 345px) {
    clip-path: path('M 0 42 C 156 86 197 9 353 42 L 354 143 L 0 143 Z');
  }
}

.cover-photo-edit {
  background-color: $grey-lighter;
}

.cover-photo { // Cards/TextBlock .projectHeader needs to match this
  min-width: 310px;
  min-height: 310px;
  height: 310px;
  max-width: 310px;
  margin-right: 1.75rem;
  overflow: hidden;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;

  .text-block {
    cursor: default;
  }

  @include mobile {
    min-width: 100%;
    min-height: 300px;
    margin: 0;
  }
}

.category-icon {
  width: 100%;
  height: 100%;
  justify-content: center;
  align-items: center;
  cursor: auto;
  background: $primary-gradient;
}

.dropdown-item {
  font-size: 0.8rem;
}

.disable-field {
  pointer-events: none;
  color: grey;
  background-color: #f2f2f2;
}

.edit-btn, .export-btn {
  padding-left: 0.75rem;
}

.fair-id {
  width: 150px;
  position: relative;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-size: 0.7rem;
  font-weight: 600;
  right: 0px;
  top: -36px;
  @media only screen and (max-width: 768px) {
    top: -40px;
    margin-top: 25px;
  }
}

.getting-started {
  font-weight: 600;
  text-transform: uppercase;
  align-items: center;
  justify-content: center;
  color: $grey-dark;
  flex: 1;

  .button {
    border-color: $grey-dark;
    border-radius: 0;

    &:hover {
      color: $primary;
      border-color: $primary;
    }
  }

  @include mobile {
    position: relative;
    margin-top: 60px !important;
  }
}

.header-top-bar {
  margin-top: 1rem;
  width: 100%;
  flex-wrap: wrap;

  .project-back-button {
    text-align: left;
    margin-top: 1rem;
    align-items: center;

    .fal {
      color: $primary;
      margin-right: .3rem;
    }

    &:hover {
      color: $link-hover !important;

      .fal {
        color: $link-hover;
      }
    }

    @include mobile-and-ipad {
      margin-left: 1rem;
    }
  }

  .header-button-container {
    width: 100%;
    flex-wrap: wrap;
    margin-top: 0.5rem;
    display: flex;
    justify-content: space-around;
  }

  .header-button {
    color: white;
    border: none;
    padding: 1rem;
    margin: 0.5rem 0.5rem 0;
    @include mobile {
      margin: 0.25rem 0.25rem 0;
    }
  }

  .feature-button {
    margin-left: auto;
    padding: 1rem;
  }

  .give-award-button, .certificate-button, .submit-button {
    background-color: $secondary;

    &:hover {
      background-color: #0096CF;
    }

    &.disabled {
      background-color: $grey-dark;
    }
  }

  .award-select, .certificate-select {
    background-color: #E8E8E8;
    border: 1px solid grey;
    border-radius: 5px;
    color: black;
    max-height: 10.5rem;
    z-index: 10;
    overflow-y: auto;
    margin-left: 0.4rem;
    position: absolute;
    cursor: pointer;
  }

  .give-award-button, .award-select {
    width: 140px;
  }

  .certificate-button, .certificate-select {
    width: 210px;
  }

  .submitted-button {
    background-color: $grey-submitted;
    cursor: text;
  }

  .approve-button {
    background-color: $success;

    &:hover {
      opacity: 0.8
    }
  }

  .approved-button {
    background-color: $grey-submitted;
    cursor: auto;

    &:hover {
      opacity: 1;
    }
  }

  .decline-button {
    background-color: $danger;

    &:hover {
      opacity: 0.8;
    }
  }

  .declined-button {
    background-color: $grey-submitted;
    cursor: auto;

    &:hover {
      opacity: 1;
    }
  }
}

.header-container {
  flex-direction: column;
  align-items: center;
  width: 100%;
  background-color: #F2F2F2;
  padding: 0;
  position: static;
}

.header-content {
  align-items: flex-start;
  padding-right: 0.5rem;
  width: 100%;
  height: 100%;
  align-items: stretch;
  @include mobile {
    flex-wrap: wrap;
    padding-right: 0;
  }
}

.header-right {
  flex-direction: column;
  margin-top: 1rem;
  width: 75%;
  max-width: 75%;
  min-height: 200px;
  @include mobile {
    width: 100%;
    max-width: 100%;
    height: 100%;
    padding: 0 1rem;
    align-content: center;
    justify-content: center;
    margin: 10px 0;
  }

  & > div {
    padding: 0.25rem 0;
    @include mobile {
      padding: 0.25rem 0 0;
    }
  }
}

.markdown-span-override p {
  margin: .5rem 0;
}

.meta-counts {
  max-width: 100%;
  flex-grow: 1;
}

.modal-background {
  background: rgba(10, 10, 10, 0.7);
}

.modal-content.saving-modal {
  height: 100%;

  .preloader {
    background: none;
  }
}

.meta-title {
  width: 100%;
}

.no-private-toggle {
  flex: 1;
}

.no-top-margin {
  margin-top: 0;
}

.owner {
  align-items: flex-start;
}

.owner-name {
  font-size: 0.8rem;
  font-weight: 600;
  align-self: center;
  display: flex;
  align-items: center;
}

.owner-icon {
  height: 1.5rem;
  width: 1.5rem;
  margin-right: 0.5rem;

  img {
    border-radius: 50%;
    height: 100%;
    width: 100%;
    max-width: 100%;
    max-height: 100%;
  }
}

.priority-size {
  height: 2.5em;
  margin-right: auto;
}

.project-header-edit {
  margin-top: .5rem !important;
  @include mobile {
    margin-top: 0;
  }
}

.project-header-start {
  margin-top: 4rem !important;
  @include mobile {
    margin-top: .5rem !important;
  }
}

.project-header {
  margin-top: 1rem;
  align-items: center;
  flex-direction: column;
  width: 100%;
  max-width: 100%;
  margin-top: 0.5rem;
  background-color: white;
}

.project-meta {
  width: 100%;
  max-width: 100%;
  position: relative;
  justify-content: space-between;
  flex-wrap: wrap;
  margin-bottom: 0.5rem;

  &.add-margin {
    margin-top: 2rem;
  }

  @include mobile {
    max-width: 100%;
    flex-wrap: wrap;
    justify-content: flex-start;
  }
}

.project-error {
  padding-right: 0.5rem;
}

.private-only {
  font-size: 0.65rem;
  align-items: center;
}

.project-categories.edit {
  width: 100%;
  align-items: center;
}

.project-categories {
  .edit {
    width: 100%;
    align-items: center;
  }

  &.wrap-categories {
    flex-wrap: wrap;
  }
}

.project-tags {
  &.wrap-tags {
    flex-wrap: wrap;
  }
}

.project-creator {
  position: relative;
  font-size: 0.7rem;
}

.project-title {
  max-width: 450px;
  word-wrap: break-word;
  @include mobile {
    max-width: 100%;
  }
  margin-bottom: 1rem;
}

.project-title input {
  div {
    font-size: 6px;
  }
}

.project-description {
  position: relative;
  font-size: 0.87rem;
  padding: 0;
  margin-bottom: 0.5rem;
  max-width: 450px;
  max-height: 120px;
  overflow-y: hidden;
  word-wrap: break-word;
  hyphens: auto;
  @include mobile {
    width: 100%;
  }

  .bottom-gradient {
    position: absolute;
    bottom: 0;
    width: 100%;
    height: 50%;
    background: linear-gradient(to top, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0));
  }

  &.seeing-more {
    max-height: none;
  }
}

.project-edit-buttons {
  width: 100%;
  display: flex;
  justify-content: flex-end;
  @media only screen and (max-width: 768px) {
    top: 8px;
    position: relative;
  }
}

.project-photo-modal {
  .modal-content {
    margin: 0;
  }

  .modal-close {
    position: absolute;
  }
}

.project-description-board-edit {
  font-size: 14px;
  display: block;

  .project-description-edit {
    width: 450px;
    display: flex;
    flex-direction: column;

    div {
      -moz-user-select: text;
      -webkit-user-select: text;
      -ms-user-select: text;
    }
  }

  .project-type {
    padding-top: 21px;
  }

  label {
    text-transform: uppercase;
    font-size: 0.7rem;
    margin-bottom: 0.3rem;
    color: $grey;
    cursor: default;
  }

  .textarea-div {
    min-height: 50px;
    border: 1px solid #dbdbdb;
    box-shadow: inset 0 1px 2px rgba(10, 10, 10, 0.1);
    border-radius: 4px;
    padding: 2px;
    hyphens: auto;
    overflow-wrap: break-word;

    &:focus, &:focus-within {
      outline: none;
      border: 1px solid $primary;
      -webkit-box-shadow: 0 0 0 0.125em rgba(234, 0, 42, 0.25);
      box-shadow: 0 0 0 0.125em rgba(234, 0, 42, 0.25);
    }
  }

  @include mobile {
    flex-wrap: wrap;
    width: 100%;
    margin: 0.5rem 0;
    .project-description-edit {
      max-width: 100%;
      min-width: 100%;
    }
    .project-type {
      padding-top: 0px;
    }
    .textarea-div {
      max-width: 100%;
      min-width: 100%;
    }
  }
}

.tool-tip {
  cursor: default;
}

.toggle-text {
  margin-left: 0.55rem;
  font-size: 0.65rem;
}

.toggle-sub {
  margin-left: 0.2rem;
  font-size: 1rem;
}

.sponsored-award {
  color: $primary;
  display: flex;
  align-items: center;

  img {
    max-height: 40px;
    max-width: 80px;
  }

  @include mobile {
    display: inline-block;
    text-align: center;
    img {
      display: block;
      margin: 0 auto;
    }
  }
}

.save-cancel-delete {
  align-items: center;
  justify-content: flex-end;
  margin-left: auto !important;
  margin-right: auto;

  & > div {
    margin-left: 0.5rem;
  }

  .edit-save, .edit-cancel {
    width: 100px;
    height: 28px;
    font-size: .7rem;
    margin: .5rem 0;
    margin-left: 1rem;
    border-radius: 0;
  }

  .edit-save.save-fr {
    width: 100px;
    margin-left: 0.5rem;
  }
}

.see-more-button {
  position: absolute;
  bottom: 0;
  right: 0;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-size: 0.7rem;
  padding-left: 2.7rem;
  padding-top: 0.5rem;
  background: linear-gradient(to right, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 1) 20%);
  outline: none;
  border: none;
  cursor: pointer;
  transition: all 0.2s;

  i {
    color: $primary;
    padding-left: 0.1rem;
    transition: all 0.2s;
  }

  &:hover {
    font-weight: 600;

    i {
      font-weight: 600;
    }
  }
}

.textarea {
  height: 4rem;
  font-size: 14px;
}

.toggle-wrap {
  align-items: center;
}

.toggle-text {
  margin-left: 0.55rem;
  font-size: 0.65rem;
}

.top-left {
  width: 150px;
}

.tags-and-categories {
  align-items: center;
  margin: auto 0;
  width: 100%;
  flex-wrap: wrap;

  & > div {
    margin: 0.15rem 0.15rem 0.15rem 0;
  }
}

.team-members {
  height: 100%;
  margin-right: 1rem;
  align-items: center;
  min-width: 0;
  max-width: inherit;
  flex-wrap: wrap;
}

.team-members-edit {
  margin: 0;
}

.top-right {
  right: 1rem;
  margin-bottom: .5rem;
  align-items: center;
  justify-content: flex-end;
}

.top-right-award {
  justify-content: space-between;
  flex-wrap: wrap-reverse;

  .private-mode {
    width: 100%;
    margin-bottom: 10px;
  }
}

.submission-deadline {
  width: 100%;
  padding: 1.1rem 0 0.5rem 0;
  font-size: 1rem;
  @include touch {
    font-size: 0.8rem;
    padding: 1.1rem 0 0.5rem 1rem;
  }
  @media (max-width: 400px) {
    font-size: 0.7rem;
  }
  &-text {
    font-weight: 800;
  }
}

.with-description {
  align-items: flex-start;
}

.project-header .template-container {
  padding-bottom: 1rem;
}

.snapshot-banner {
  background-color: white;
  width: 100%;
  max-width: 800px;
  padding: 0.9rem 1.8rem;
  margin: 0.5rem 0;
}

.snapshot-message {
  text-align: center;
  font-size: 0.8rem;
}
@media only screen and (max-width: 767px) {
  .header-container {
    padding-left: 15px;
    padding-right: 15px;
  }
}
</style>
